/*
 * Purpose: Passwordless sign-in page — multimodal auth + magic-link callback
 * Last modified: 2026-07-10 | Agent
 * Completeness: 98
 */
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { BrandMark } from '@/components/brand/BrandMark';
import { PasswordlessSignInPanel } from '@/components/auth/PasswordlessSignInPanel';
import { TenantPickerPanel } from '@/components/auth/TenantPickerPanel';
import { PlanSelectionPanel } from '@/components/auth/PlanSelectionPanel';
import { AUTH_CONFIG, authUrl } from '@/lib/auth-config';
import { authFetchErrorMessage } from '@/lib/apiBase';
import { bootstrapAuthSession } from '@/lib/authCompleteBootstrap';
import { PRODUCT_NAME } from '@/lib/productTerms';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';
import { useEffect, useState } from 'react';

export default function AuthSignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || AUTH_CONFIG.postLoginPath;
  const magicToken = params.get('magic');
  const oauthError = params.get('error');
  const authed = useAuthStore((s) => s.authed);
  const tenantPickRequired = useAuthStore((s) => s.tenantPickRequired);
  const planPickRequired = useAuthStore((s) => s.planPickRequired);
  const activeTenant = useAuthStore((s) => s.activeTenant);
  const hydrate = useAuthStore((s) => s.hydrate);
  const [magicBusy, setMagicBusy] = useState(Boolean(magicToken));
  const [magicError, setMagicError] = useState('');

  useEffect(() => {
    if (authed && activeTenant && !tenantPickRequired && !planPickRequired) {
      navigate(next, { replace: true });
    }
  }, [authed, activeTenant, tenantPickRequired, planPickRequired, navigate, next]);

  useEffect(() => {
    if (!magicToken || AUTH_CONFIG.mock) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(authUrl(AUTH_CONFIG.endpoints.magicLinkVerify), {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: magicToken }),
        });
        const json = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
        if (!res.ok) throw new Error(json.error || 'Magic link invalid');

        const boot = await bootstrapAuthSession(hydrate, useAuthStore.getState);
        if (!boot.authed) {
          throw new Error('Magic link sign-in did not establish a session — request a new link.');
        }
        if (!cancelled && !boot.tenantPickRequired && !boot.planPickRequired) {
          navigate(next, { replace: true });
        }
      } catch (e) {
        if (!cancelled) setMagicError(authFetchErrorMessage(e));
      } finally {
        if (!cancelled) setMagicBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [magicToken, hydrate, navigate, next]);

  return (
    <div className="auth-gate-overlay min-h-screen flex flex-col bg-[var(--auth-bg)]">
      <header className="shrink-0 border-b border-[var(--auth-border)]/60 bg-[var(--auth-surface)]/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <BrandMark size={24} className="shrink-0" ghostAccent="darker" />
            <span className="ab-brand-wordmark text-sm text-[var(--auth-fg)]">{PRODUCT_NAME}</span>
          </Link>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Home
            </Link>
          </Button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4 sm:p-8">
        {magicBusy ? (
          <p className="text-sm text-muted-foreground">Signing you in…</p>
        ) : magicError || oauthError ? (
          <div className="max-w-md text-center space-y-3">
            <p className="text-sm text-destructive">{magicError || oauthError}</p>
            <PasswordlessSignInPanel
              next={next}
              onSuccess={() => navigate(next, { replace: true })}
            />
          </div>
        ) : authed && tenantPickRequired ? (
          <TenantPickerPanel next={next} />
        ) : authed && planPickRequired ? (
          <PlanSelectionPanel next={next} />
        ) : (
          <PasswordlessSignInPanel
            next={next}
            onSuccess={() => navigate(next, { replace: true })}
          />
        )}
      </main>
    </div>
  );
}
